import { MonetaryAmount } from '@components'
import type { LedgerJson, ReportRequestBody } from '@query'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { FaChevronDown, FaChevronRight } from 'react-icons/fa'
import { LedgerAccountEntriesPanel } from './LedgerAccountEntriesPanel'
import {
  AccountCode,
  Caret,
  ColumnHeaderRow,
  InlineAccountCode,
  LineItemHeader,
  ReportRow,
  RowLabel,
  RowLabelText,
  RowMeta,
  ValueCell,
  ValueHeader
} from './interactive.styled'
import { ledgerTableMinWidthPx } from './periodUtils'
import { LedgerListEmptyState } from './ReportListEmptyState'
import { ReportCardLayout } from './ReportCardLayout'
import { ReportLeadingGutter } from './ReportLeadingGutter'
import type { ReportExpansion } from './interactiveReportsSession'
import { useOnTokenIncrement } from './useOnTokenIncrement'

type Props = {
  slug: string
  reportBody: ReportRequestBody
  data: LedgerJson
  expansion: ReportExpansion
  onExpansionChange: (patch: Partial<ReportExpansion>) => void
  expandAllToken: number
  collapseAllToken: number
  currency?: string
  isRefreshing?: boolean
  isStale?: boolean
  hasTagFilter?: boolean
}

const entryCount = (count?: number, entries?: unknown[]) => count ?? entries?.length ?? 0

export const LedgerView: React.FC<Props> = ({
  slug,
  reportBody,
  data,
  expansion,
  onExpansionChange,
  expandAllToken,
  collapseAllToken,
  currency,
  isRefreshing = false,
  isStale = false,
  hasTagFilter = false
}) => {
  const [t] = useTranslation()
  const expanded = expansion.items

  useOnTokenIncrement(expandAllToken, () => {
    const next: Record<string, boolean> = {}
    ;(data.accounts ?? []).forEach(account => {
      next[account.number] = true
    })
    onExpansionChange({ items: next })
  })

  useOnTokenIncrement(collapseAllToken, () => {
    onExpansionChange({ items: {} })
  })

  const accounts = data.accounts ?? []

  return (
    <ReportCardLayout
      title={t('constants.reportType.ledger')}
      isRefreshing={isRefreshing}
      isStale={isStale}
      footNote={t('reports.interactive.footNote.ledger')}
      tableMinWidth={ledgerTableMinWidthPx()}
    >
      <ColumnHeaderRow>
        <ReportLeadingGutter />
        <LineItemHeader>{t('reports.interactive.account')}</LineItemHeader>
        <ValueHeader $width="120px">{t('reports.interactive.opening')}</ValueHeader>
        <ValueHeader $width="120px">{t('reports.interactive.debit')}</ValueHeader>
        <ValueHeader $width="120px">{t('reports.interactive.credit')}</ValueHeader>
        <ValueHeader>{t('reports.interactive.closing')}</ValueHeader>
      </ColumnHeaderRow>

      {accounts.length === 0 ? (
        <LedgerListEmptyState
          totals={data.totals}
          currency={currency}
          hasTagFilter={hasTagFilter}
        />
      ) : (
        accounts.map(account => {
          const open = !!expanded[account.number]
          const debit = account.totals?.debet ?? 0
          const credit = account.totals?.credit ?? 0
          const closing = account.closing_balance ?? account.opening_balance
          const count = entryCount(account.entries_count, account.entries)

          return (
            <React.Fragment key={account.number}>
              <ReportRow
                $clickable
                $bg={open ? '#f7fafd' : undefined}
                onClick={() =>
                  onExpansionChange({
                    items: { ...expanded, [account.number]: !expanded[account.number] }
                  })
                }
              >
                <AccountCode />
                <RowLabel>
                  <Caret>{open ? <FaChevronDown /> : <FaChevronRight />}</Caret>
                  <RowLabelText>
                    <InlineAccountCode>{account.number}</InlineAccountCode>
                    {account.name}
                    <RowMeta>{t('reports.interactive.entryCount', { count })}</RowMeta>
                  </RowLabelText>
                </RowLabel>
                <ValueCell $width="120px">
                  <MonetaryAmount value={account.opening_balance} currency={currency} />
                </ValueCell>
                <ValueCell $width="120px">
                  <MonetaryAmount value={debit} currency={currency} />
                </ValueCell>
                <ValueCell $width="120px">
                  <MonetaryAmount value={credit} currency={currency} />
                </ValueCell>
                <ValueCell $strong>
                  <MonetaryAmount value={closing} currency={currency} />
                </ValueCell>
              </ReportRow>
              {open && (
                <LedgerAccountEntriesPanel
                  slug={slug}
                  reportBody={reportBody}
                  accountNumber={account.number}
                  currency={currency}
                />
              )}
            </React.Fragment>
          )
        })
      )}
    </ReportCardLayout>
  )
}
